import { createMachine, assign, setup } from "xstate";
import { ExemptionQuestions } from "utils/types/exemption-questions";
import { commonGuards } from "./_commonGuards";
import moment from "moment";

export const georgiaExemptionQuestionFlowMachine = setup({
	types: {
		context: {} as ExemptionQuestions.FlowContext,
		events: {} as ExemptionQuestions.FlowEvent,
		input: {} as Partial<ExemptionQuestions.FlowContext>,
	},
	actions: {
		updateContext: assign(({ context, event }) => {
			if ("context" in event) {
				return {
					...context,
					...event.context,
				};
			}
			return context;
		}),
	},
	guards: {
		isFultonCounty: ({ context, event }) => context.county === "Fulton",
		isDeKalbCounty: ({ context, event }) => context.county === "DeKalb",
		noCurrentHomesteadExemption: ({ context, event }) =>
			[
				context.primaryResidenceValue === true,
				commonGuards.currentlyReceivingNoOrNotSure(context),
			].every(value => value === true),
	},
	delays: {},
}).createMachine({
	id: "GEORGIA_EXEMPTION",
	context: ({ input }) => ({ ...input }) as ExemptionQuestions.FlowContext,
	initial: "IDLE",
	states: {
		IDLE: {},
		INIT: {
			always: [
				{
					target: "PRIMARY_RESIDENCE",
					guard: ({ context, event }) =>
						context.primaryResidenceValue === undefined,
				},
				{
					target: "CURRENTLY_RECEIVING",
					guard: ({ context, event }) =>
						context.currentlyReceivingValue === undefined,
				},
				{
					target: "DOB",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.dobValue === undefined,
				},
				{
					target: "RARE_SITUATIONS",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						[
							context.isVeteran === true,
							context.specialFieldsValue
								?.surviving_spouse_or_child_of_a_veteran === true,
							context.survivingSpouseOfFirstResponderKilledInLineOfDuty ===
								true,
							context.specialFieldsValue?.is_active_duty_personnel ===
								undefined,
							context.noneOfTheAbove === undefined,
						].some(value => value === true),
				},
				{
					target: "SSN_TIN",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.owner1HasEnteredSsnTin === undefined,
				},
				{
					target: "MARITAL_STATUS",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.maritalStatusValue === undefined,
				},
				{
					target: "OWNERS",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.ownersValue === undefined,
				},
				{
					target: "ADDITIONAL_OWNER",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						commonGuards.hasAdditionalOwners(context) &&
						[
							context.additionalOwnerFirstNameValue,
							context.additionalOwnerLastNameValue,
							context.additionalOwnerDobValue,
						].some(v => v === undefined),
				},
				{
					target: "SSN_TIN2",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						commonGuards.hasAdditionalOwners(context) &&
						context.owner2HasEnteredSsnTin === undefined,
				},
				{
					target: "DL_UPLOAD",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						!context.ownerHasUploadedDlFile,
				},
				{
					target: "DL2_UPLOAD",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						commonGuards.hasAdditionalOwners(context) &&
						!!context.ownerHasUploadedDlFile &&
						!context.owner2HasUploadedDlFile,
				},
				{
					target: "VEHICLE_INFO",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.county === "Fulton" &&
						(context.specialFieldsValue?.vehicle_info === undefined ||
							(context.specialFieldsValue?.vehicle_info[0].state_where_owned ===
								"GA" &&
								(context.specialFieldsValue?.vehicle_info[0].make ===
									undefined ||
									context.specialFieldsValue?.vehicle_info[0].model ===
										undefined ||
									context.specialFieldsValue?.vehicle_info[0].year ===
										undefined ||
									context.specialFieldsValue?.vehicle_info[0]
										.license_plate_number === undefined ||
									context.specialFieldsValue?.vehicle_info[0]
										.county_where_registered === undefined ||
									context.specialFieldsValue?.vehicle_info[0]
										.state_where_registered === undefined))),
				},
				{
					target: "VEHICLE_REGISTRATIONS",
					guard: ({ context, event }) => {
						if (!commonGuards.currentlyReceivingNoOrNotSure(context)) {
							return false;
						}

						if (context.county === "DeKalb") {
							return (
								context.specialFieldsValue?.vehicle_registrations === undefined
							);
						}

						if (context.county === "Fulton") {
							return !context.specialFieldsValue?.vehicle_registration_uploaded;
						}

						return false;
					},
				},
				{
					target: "AGREEMENT",
					guard: ({ context, event }) =>
						[
							context.primaryResidenceValue === true,
							commonGuards.currentlyReceivingNoOrNotSure(context),
						].every(value => value === true),
				},
			],
		},
		PRIMARY_RESIDENCE: {
			entry: [
				assign({
					exemptionsProgress: context => 0,
				}),
			],
			on: {
				next: [
					{
						target: "CURRENTLY_RECEIVING",
						guard: ({ context, event }) =>
							commonGuards.isPrimaryResidence(context),
					},
					{
						target: "NON_AGREEMENT_CONFIRMATION",
						guard: ({ context, event }) =>
							commonGuards.isNotPrimaryResidence(context),
					},
				],
			},
		},
		NON_AGREEMENT_CONFIRMATION: {
			entry: [
				assign({
					exemptionsProgress: context => 100,
				}),
			],
		},
		CURRENTLY_RECEIVING: {
			entry: [
				assign({
					exemptionsProgress: context => (1 / 13) * 100,
				}),
			],
			on: {
				back: {
					target: "PRIMARY_RESIDENCE",
					actions: assign({
						currentlyReceivingValue: context => undefined,
					}),
				},
				next: [
					{
						target: "DOB",
						guard: ({ context, event }) =>
							commonGuards.currentlyReceivingNoOrNotSure(context),
					},
					{
						target: "NON_AGREEMENT_CONFIRMATION",
					},
				],
			},
		},
		DOB: {
			entry: [
				assign({
					exemptionsProgress: context => (3 / 13) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "CURRENTLY_RECEIVING",
						actions: assign({
							dobValue: context => undefined,
						}),
					},
				],
				next: [
					{
						target: "NON_AGREEMENT_CONFIRMATION",
						guard: ({ context, event }) =>
							moment().diff(context.dobValue, "years") >= 62,
					},
					{
						target: "RARE_SITUATIONS",
					},
				],
			},
		},
		RARE_SITUATIONS: {
			entry: [
				assign({
					exemptionsProgress: context => (4 / 13) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "DOB",
						actions: assign({
							isVeteran: context => undefined,
							specialFieldsValue: ({ context, event }) => ({
								...context.specialFieldsValue,
								surviving_spouse_or_child_of_a_veteran: undefined,
								is_active_duty_personnel: undefined,
							}),
							survivingSpouseOfFirstResponderKilledInLineOfDuty: context =>
								undefined,
							noneOfTheAbove: context => undefined,
						}),
					},
				],
				next: [
					{
						target: "NON_AGREEMENT_CONFIRMATION",
						guard: ({ context, event }) =>
							context.isVeteran === true ||
							context.specialFieldsValue
								?.surviving_spouse_or_child_of_a_veteran === true ||
							context.survivingSpouseOfFirstResponderKilledInLineOfDuty ===
								true,
					},
					{
						target: "SSN_TIN",
					},
				],
			},
		},
		SSN_TIN: {
			entry: [
				assign({
					exemptionsProgress: context => (5 / 13) * 100,
				}),
			],
			on: {
				back: {
					target: "RARE_SITUATIONS",
					actions: assign({
						owner1SsnTin: context => undefined,
						owner1HasEnteredSsnTin: context => undefined,
					}),
				},
				next: {
					target: "MARITAL_STATUS",
				},
			},
		},
		MARITAL_STATUS: {
			entry: [
				assign({
					exemptionsProgress: context => (5.5 / 13) * 100,
				}),
			],
			on: {
				back: {
					target: "SSN_TIN",
					actions: assign({
						maritalStatusValue: context => undefined,
					}),
				},
				next: {
					target: "OWNERS",
				},
			},
		},
		OWNERS: {
			entry: [
				assign({
					exemptionsProgress: context => (6 / 13) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "MARITAL_STATUS",
					},
				],
				next: [
					{
						target: "ADDITIONAL_OWNER",
						guard: ({ context, event }) =>
							commonGuards.hasAdditionalOwners(context),
					},
					{
						target: "DL_UPLOAD",
					},
				],
			},
		},
		ADDITIONAL_OWNER: {
			entry: [
				assign({
					exemptionsProgress: context => (6.5 / 13) * 100,
				}),
			],
			on: {
				back: {
					target: "OWNERS",
					actions: assign({
						additionalOwnerFirstNameValue: context => undefined,
						additionalOwnerLastNameValue: context => undefined,
						additionalOwnerDobValue: context => undefined,
					}),
				},
				next: [
					{
						target: "SSN_TIN2",
					},
					{
						target: "DL_UPLOAD",
					},
				],
			},
		},
		SSN_TIN2: {
			entry: [
				assign({
					exemptionsProgress: context => (7 / 13) * 100,
				}),
			],
			on: {
				back: {
					target: "ADDITIONAL_OWNER",
					actions: assign({
						owner2SsnTin: context => undefined,
						owner2HasEnteredSsnTin: context => undefined,
					}),
				},
				next: {
					target: "DL_UPLOAD",
				},
			},
		},
		DL_UPLOAD: {
			entry: [
				assign({
					exemptionsProgress: context => (8 / 13) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "OWNERS",
						guard: ({ context, event }) => commonGuards.isSoleOwner(context),
						actions: assign({
							ownerDlFilesToUpload: context => [],
							ownerDlExemptValue: context => undefined,
						}),
					},
					{
						target: "SSN_TIN2",
						guard: ({ context, event }) =>
							commonGuards.hasAdditionalOwners(context),
						actions: assign({
							ownerDlFilesToUpload: context => [],
							ownerDlExemptValue: context => undefined,
						}),
					},
				],
				next: [
					{
						target: "DL2_UPLOAD",
						guard: ({ context, event }) =>
							commonGuards.hasAdditionalOwners(context),
					},
					{
						target: "VEHICLE_INFO",
						guard: "isFultonCounty",
					},
					{
						target: "VEHICLE_REGISTRATIONS",
					},
				],
			},
		},
		DL2_UPLOAD: {
			entry: [
				assign({
					exemptionsProgress: context => (8.5 / 13) * 100,
				}),
			],
			on: {
				back: {
					target: "DL_UPLOAD",
					actions: assign({
						owner2DlFilesToUpload: context => [],
						owner2DlExemptValue: context => undefined,
					}),
				},
				next: [
					{
						target: "VEHICLE_INFO",
						guard: "isFultonCounty",
					},
					{
						target: "VEHICLE_REGISTRATIONS",
					},
				],
			},
		},
		VEHICLE_REGISTRATIONS: {
			entry: [
				assign({
					exemptionsProgress: context => (8.75 / 13) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "DL_UPLOAD",
						guard: ({ context, event }) =>
							context.county === "DeKalb" && commonGuards.isSoleOwner(context),
						actions: assign({
							specialFieldsValue: ({ context, event }) => ({
								...context.specialFieldsValue,
								vehicle_registrations: undefined,
							}),
						}),
					},
					{
						target: "DL2_UPLOAD",
						guard: ({ context, event }) =>
							context.county === "DeKalb" &&
							commonGuards.hasAdditionalOwners(context),
						actions: assign({
							specialFieldsValue: ({ context, event }) => ({
								...context.specialFieldsValue,
								vehicle_registrations: undefined,
							}),
						}),
					},
					{
						target: "VEHICLE_INFO",
						guard: ({ context, event }) => context.county === "Fulton",
						actions: assign({
							specialFieldsValue: ({ context, event }) => ({
								...context.specialFieldsValue,
								vehicle_registrations: undefined,
							}),
						}),
					},
				],
				next: {
					target: "AGREEMENT",
				},
			},
		},
		VEHICLE_INFO: {
			entry: [
				assign({
					exemptionsProgress: context => (8.75 / 13) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "DL_UPLOAD",
						guard: ({ context, event }) =>
							context.county === "Fulton" && commonGuards.isSoleOwner(context),
						actions: assign({
							specialFieldsValue: ({ context, event }) => ({
								...context.specialFieldsValue,
								vehicle_info: undefined,
							}),
						}),
					},
					{
						target: "DL2_UPLOAD",
						guard: ({ context, event }) =>
							context.county === "Fulton" &&
							commonGuards.hasAdditionalOwners(context),
						actions: assign({
							specialFieldsValue: ({ context, event }) => ({
								...context.specialFieldsValue,
								vehicle_info: undefined,
							}),
						}),
					},
				],
				next: [
					{
						target: "VEHICLE_REGISTRATIONS",
						guard: ({ context }) =>
							!!context.specialFieldsValue?.vehicle_info?.length &&
							!!context.specialFieldsValue.vehicle_info[0].state_where_owned,
					},
					{
						target: "AGREEMENT",
					},
				],
			},
		},
		AGREEMENT: {
			entry: [
				assign({
					exemptionsProgress: context => (12 / 13) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "VEHICLE_REGISTRATIONS",
						guard: ({ context }) =>
							context.county === "DeKalb" ||
							(context.county === "Fulton" &&
								context.specialFieldsValue?.vehicle_info?.[0]
									.state_where_registered === "GA"),
					},
					{
						target: "VEHICLE_INFO",
					},
				],
				next: {
					target: "MAIN_CONFIRMATION",
				},
			},
		},
		MAIN_CONFIRMATION: {
			entry: [
				assign({
					exemptionsProgress: context => 100,
				}),
			],
			on: {
				next: {
					target: "MAIN_CONFIRMATION",
				},
			},
		},
	},
	on: {
		update_context: {
			actions: "updateContext",
		},
		restart: {
			target: ".INIT",
		},
	},
});
